import { ApiError, parseRetryAfter } from './http-error';
import { getAccessToken, getCurrentTenant } from './tenant';

/** Base de l'API, injectée à l'exécution par /env.js (une seule image pour tous les environnements). */
let baseUrl = '/api/v1';

export function setApiBaseUrl(url: string): void {
  baseUrl = url.replace(/\/+$/, '');
}

/** Préfixe des médias types propriétaires du contrat : `application/vnd.graal.systems.v1.<ressource>+json`. */
export const VND_PREFIX = 'application/vnd.graal.systems.v1.';

/**
 * `Accept` par défaut. Mesuré le 2026-09-25 sur `graal-api` (main 9578bd0) :
 * `Accept: application/json` → **404** sur `GET /api/v1/projects`, `application/*+json` → 200.
 * Les routeurs WebFlux du backend posent `RequestPredicates.accept(<vnd>)` sur presque toutes les
 * routes, et Spring ne juge pas `application/json` compatible avec `application/vnd.x+json`
 * (`MimeType.isCompatibleWith` n'accepte le suffixe qu'avec un joker). Le joker de sous-type
 * `*+json` couvre à la fois les routes `vnd` et les rares routes en `application/json`
 * (`/permissions`, `/roles/{id}/permissions`).
 */
export const ACCEPT_JSON = 'application/*+json';

export const vndMediaType = (resource: string): string => `${VND_PREFIX}${resource}+json`;

export type RequestConfig = {
  url: string;
  method: string;
  params?: Record<string, unknown> | undefined;
  data?: unknown;
  /**
   * Corps déjà encodé (`FormData` pour `POST /code?download`) : envoyé tel quel, sans
   * `JSON.stringify` ni `Content-Type` (le navigateur pose la frontière multipart).
   */
  body?: FormData | Blob | string | undefined;
  headers?: Record<string, string> | undefined;
  signal?: AbortSignal | undefined;
  responseType?: 'json' | 'text' | 'blob' | undefined;
  /**
   * Ressource du contrat (`project`, `job`, `role`…). Les écritures du backend exigent le média
   * type EXACT en `Content-Type` (`RequestPredicates.contentType(<vnd>)`, sans joker possible sur
   * une requête) : `POST /projects` en `application/json` répond 404. Quand elle est donnée, la
   * ressource fixe aussi l'`Accept`.
   */
  resource?: string | undefined;
};

function buildUrl(config: RequestConfig): string {
  // Neuf chemins du contrat contiennent déjà une query string (`/code?download`, `/permissions?metadata`…) :
  // on la préserve et on ajoute les paramètres derrière le bon séparateur.
  const [path, embedded] = config.url.split('?', 2);
  const search = new URLSearchParams(embedded ?? '');
  for (const [key, value] of Object.entries(config.params ?? {})) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) value.forEach((item) => search.append(key, String(item)));
    else search.append(key, String(value));
  }
  const qs = search.toString();
  return `${baseUrl}${path}${qs ? `?${qs}` : ''}`;
}

/**
 * Mutator unique du client orval : base d'exécution, `X-Tenant`, `Authorization`, media types
 * propriétaires (`application/vnd.graal.systems.v1.*+json`) et JSON Patch RFC 6902.
 * Aucun appel ne pose `X-Tenant` à la main.
 */
export async function apiFetch<T>(config: RequestConfig): Promise<T> {
  return (await apiRequest<T>(config)).data;
}

/**
 * Même politique qu'`apiFetch`, mais rend aussi la réponse : certaines opérations du contrat
 * portent leur pagination dans un en-tête et non dans le corps (`X-Cursor` sur
 * `GET /jobs/{jobId}/runs/{runId}/logs`, lu par le LogViewer pour le suivi incrémental).
 */
export async function apiRequest<T>(config: RequestConfig): Promise<{ data: T; response: Response }> {
  const headers = new Headers(config.headers);
  const tenant = getCurrentTenant();
  if (tenant) headers.set('X-Tenant', tenant);
  const token = getAccessToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);
  if (!headers.has('Accept')) {
    headers.set(
      'Accept',
      config.responseType === 'blob' || config.responseType === 'text'
        ? '*/*'
        : config.resource
          ? vndMediaType(config.resource)
          : ACCEPT_JSON,
    );
  }
  const isPatch = config.method.toUpperCase() === 'PATCH';
  if (config.data !== undefined && !headers.has('Content-Type')) {
    headers.set(
      'Content-Type',
      isPatch
        ? 'application/json-patch+json'
        : config.resource
          ? vndMediaType(config.resource)
          : 'application/json',
    );
  }

  const response = await fetch(buildUrl(config), {
    method: config.method.toUpperCase(),
    headers,
    ...(config.body !== undefined
      ? { body: config.body }
      : config.data === undefined
        ? {}
        : { body: JSON.stringify(config.data) }),
    ...(config.signal ? { signal: config.signal } : {}),
    credentials: 'omit',
  });

  const payload = await readBody(response, config.responseType);
  if (!response.ok)
    throw new ApiError(
      response.status,
      payload,
      undefined,
      parseRetryAfter(response.headers.get('Retry-After')),
      response.headers.get('X-Request-Id'),
    );
  return { data: payload as T, response };
}

async function readBody(response: Response, responseType: RequestConfig['responseType']): Promise<unknown> {
  if (response.status === 204 || response.status === 202) return undefined;
  if (responseType === 'blob') return response.blob();
  if (responseType === 'text') return response.text();
  const contentType = response.headers.get('Content-Type') ?? '';
  // Le backend renvoie des media types propriétaires : application/vnd.graal.systems.v1.<ressource>+json.
  if (/\bjson\b/.test(contentType)) {
    const text = await response.text();
    return text ? (JSON.parse(text) as unknown) : undefined;
  }
  return response.text();
}
